import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../../../lib/supabase';

export const usePermissions = () => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState(null);
  const [userLevel, setUserLevel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        setPermissions(null);
        setUserLevel(null);
        setLoading(false);
        return;
      }

      try {
        // Buscar dados do usuário na tabela funcionariousuario
        const { data, error } = await supabase
          .from('funcionariousuario')
          .select('nivel_acesso, permissoes, ativo, setor')
          .eq('uid', user.id)
          .single();

        if (error) {
          console.error('Erro ao carregar permissões:', error);
          // Se não encontrar o usuário na tabela, assumir que é admin (para compatibilidade)
          setPermissions({
            obras: true,
            'obras-fixas': true,
            'fossas-septicas': true,
            'postes-iluminacao': true,
            'gestao-usuarios': true,
            'controle-demandas': true
          });
          setUserLevel('admin');
        } else if (data && data.ativo && data.setor === null) {
          // Usuário ativo e não é colaborador (setor é null)
          setPermissions(data.permissoes || {});
          setUserLevel(data.nivel_acesso);
        } else {
          // Usuário inativo ou é colaborador
          setPermissions({});
          setUserLevel(null);
          
          // Se é colaborador ou usuário inativo, fazer logout
          if (data && (!data.ativo || data.setor !== null)) {
            console.log('Usuário inativo ou colaborador detectado, fazendo logout');
            supabase.auth.signOut();
          }
        }
      } catch (error) {
        console.error('Erro ao carregar permissões:', error);
        // Em caso de erro, dar acesso total (para não quebrar o sistema)
        setPermissions({
          obras: true,
          'obras-fixas': true,
          'fossas-septicas': true,
          'postes-iluminacao': true,
          'gestao-usuarios': true,
          'controle-demandas': true
        });
        setUserLevel('admin');
      } finally {
        setLoading(false);
      }
    };

    loadUserPermissions();
  }, [user]);

  // Função para verificar se tem permissão para um módulo específico
  const hasPermission = (module) => {
    if (!permissions) return false;
    if (userLevel === 'admin') return true; // Admin tem acesso a tudo
    
    return permissions[module] === true;
  };

  // Função para verificar se é admin
  const isAdmin = () => {
    return userLevel === 'admin';
  };

  // Função para verificar se é operador
  const isOperator = () => {
    return userLevel === 'operador';
  };

  // Função para verificar se é visualizador
  const isViewer = () => {
    return userLevel === 'visualizador';
  };

  // Função para obter módulos com permissão
  const getPermittedModules = () => {
    if (!permissions) return [];
    
    if (userLevel === 'admin') {
      return ['obras', 'obras-fixas', 'fossas-septicas', 'postes-iluminacao', 'mapa-integrado', 'gestao-usuarios', 'controle-demandas'];
    }

    return Object.keys(permissions).filter(module => permissions[module] === true);
  };

  return {
    permissions,
    userLevel,
    loading,
    hasPermission,
    isAdmin,
    isOperator,
    isViewer,
    getPermittedModules
  };
};